// client/src/pages/HRAttendance.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import API from '../api/client';
import {
  Box, Typography, Chip, CircularProgress, TextField,
  Tabs, Tab, Avatar, Button, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Card, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid
} from '@mui/material';
import {
  Search, Users, CalendarDays, RefreshCw, AlertCircle, 
  UserCheck, Clock, Mail, BarChart3, Eye, Calendar, ArrowRight
} from 'lucide-react';

const HRAttendance = () => {
  const [payloadData, setPayloadData] = useState({ employees: [], attendance: [], leaves: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Drill-down Modal State
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(true);

  const getTodayISTString = () => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date());
  };

  const [selectedMonth, setSelectedMonth] = useState(getTodayISTString().substring(0, 7)); // YYYY-MM

  const fetchAllEmployeeLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get(`/attendance/master-logs?month=${selectedMonth}`);
      if (response.data && response.data.success) {
        setPayloadData({
          employees: response.data.employees || [],
          attendance: response.data.attendance || [],
          leaves: response.data.leaves || []
        });
      }
    } catch (err) {
      console.error('Failed to load attendance roster:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchAllEmployeeLogs();
  }, [fetchAllEmployeeLogs]);

  const calendarMetrics = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const totalDays = new Date(year, month, 0).getDate();
    let tuesdays = 0;
    for (let d = 1; d <= totalDays; d++) {
      if (new Date(year, month - 1, d).getDay() === 2) tuesdays++;
    }
    return { totalDays, totalTuesdays: tuesdays, year, month };
  }, [selectedMonth]);

  const processedPayrollRoster = useMemo(() => {
    const { totalDays, totalTuesdays, year, month } = calendarMetrics;
    const { employees, attendance, leaves } = payloadData;

    const todayLocalStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date());

    const convertToISTDateString = (dateInput) => {
      if (!dateInput) return '';
      try {
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
        }).format(new Date(dateInput));
      } catch (e) {
        return dateInput.split('T')[0];
      }
    };

    return employees.map(emp => {
      const empIdStr = String(emp.id);
      const empAttendance = attendance.filter(log => String(log.employee_id) === empIdStr);
      const empApprovedLeaves = leaves.filter(lv => String(lv.employee_id) === empIdStr);

      let daysPresent = 0;
      let daysLate = 0;
      let todayClockIn = '—';
      let todayClockOut = '—';
      let liveStatus = 'absent';

      const leavesMap = new Set();
      empApprovedLeaves.forEach(lv => {
        const startStr = convertToISTDateString(lv.start_date);
        const endStr = convertToISTDateString(lv.end_date);
        const start = new Date(startStr + "T00:00:00");
        const end = new Date(endStr + "T23:59:59");

        for (let d = 1; d <= totalDays; d++) {
          const currentLoopDate = new Date(year, month - 1, d);
          if (currentLoopDate >= start && currentLoopDate <= end) {
            if (currentLoopDate.getDay() !== 2) {
              leavesMap.add(d);
            }
          }
        }
      });
      const daysLeaveAccumulated = leavesMap.size;

      empAttendance.forEach(log => {
        const logDateStr = convertToISTDateString(log.date);
        if (!logDateStr) return;

        let calculatedStatus = String(log.status || 'Present').trim().toLowerCase();
        if (log.clock_in) {
          const [hh, mm] = log.clock_in.split(':').map(Number);
          if ((hh * 60 + mm) > 675) calculatedStatus = 'late';
        }

        if (['present', 'on time', 'remote'].includes(calculatedStatus)) {
          daysPresent++;
        } else if (calculatedStatus === 'late') {
          daysPresent++;
          daysLate++;
        }

        if (logDateStr === todayLocalStr) {
          todayClockIn = log.clock_in || '—';
          todayClockOut = log.clock_out || '—';
          liveStatus = calculatedStatus;
        }
      });

      const systemToday = new Date();
      const checkCurrentDayNum = systemToday.getDate();

      if (systemToday.getDay() === 2 && todayLocalStr.substring(0, 7) === selectedMonth) {
        liveStatus = 'weekoff';
      } else if (liveStatus === 'absent' && leavesMap.has(checkCurrentDayNum) && todayLocalStr.substring(0, 7) === selectedMonth) {
        liveStatus = 'leave';
      }

      const unexcusedAbsences = Math.max(0, totalDays - totalTuesdays - daysPresent - daysLeaveAccumulated);
      const payableDaysTotal = daysPresent + totalTuesdays;

      const historicalLogs = [];
      for (let d = 1; d <= totalDays; d++) {
        const paddingDay = String(d).padStart(2, '0');
        const loopDateStr = `${selectedMonth}-${paddingDay}`;
        const targetPunch = empAttendance.find(log => convertToISTDateString(log.date) === loopDateStr);

        let displayStatus = 'Absent';
        if (new Date(year, month - 1, d).getDay() === 2) {
          displayStatus = 'Weekly Off';
        } else if (targetPunch) {
          if (targetPunch.clock_in) {
            const [h, m] = targetPunch.clock_in.split(':').map(Number);
            displayStatus = (h * 60 + m) > 675 ? 'Late' : 'Present';
          }
        } else if (leavesMap.has(d)) {
          displayStatus = 'Leave';
        }

        historicalLogs.push({
          date: loopDateStr,
          clockIn: targetPunch?.clock_in || '—',
          clockOut: targetPunch?.clock_out || '—',
          status: displayStatus
        });
      }

      return {
        id: emp.id,
        name: emp.name,
        email: emp.email || `${emp.name.toLowerCase().replace(/\s+/g, '')}@company.com`,
        currentStatus: liveStatus,
        clockInToday: todayClockIn,
        clockOutToday: todayClockOut,
        daysPresent,
        daysLate,
        daysLeave: daysLeaveAccumulated,
        weekoffs: totalTuesdays,
        unexcusedAbsences,
        payableDaysTotal,
        historicalLogs: historicalLogs.reverse()
      };
    });
  }, [payloadData, calendarMetrics, selectedMonth]);

  useEffect(() => {
    if (processedPayrollRoster.length > 0) {
      const stillExists = processedPayrollRoster.find(e => e.id === selectedEmployee?.id);
      setSelectedEmployee(stillExists || processedPayrollRoster[0]);
    } else {
      setSelectedEmployee(null);
    }
  }, [processedPayrollRoster, selectedEmployee?.id]);

  const filteredRoster = useMemo(() => {
    return processedPayrollRoster.filter(emp => {
      const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      const activeStatus = emp.currentStatus.toLowerCase();
      if (currentTab === 'present') return ['on time', 'present', 'remote'].includes(activeStatus);
      if (currentTab === 'late') return activeStatus === 'late';
      if (currentTab === 'leave') return ['leave', 'absent', 'weekly off'].includes(activeStatus);
      return true;
    });
  }, [processedPayrollRoster, searchQuery, currentTab]);

  const liveCounts = useMemo(() => {
    let present = 0, late = 0, leave = 0, absent = 0;
    processedPayrollRoster.forEach(p => {
      const status = p.currentStatus.toLowerCase();
      if (['on time', 'present', 'remote'].includes(status)) present++;
      else if (status === 'late') late++;
      else if (['leave'].includes(status)) leave++;
      else if (status === 'absent') absent++;
    });
    return { present, late, leave, absent, total: processedPayrollRoster.length };
  }, [processedPayrollRoster]);

  const getStatusConfig = (status) => {
    const formatted = status.toLowerCase();
    if (['present', 'on time', 'remote'].includes(formatted)) {
      return { label: 'Present', color: 'success', bg: '#e6f4ea', text: '#137333' };
    }
    if (formatted === 'late') {
      return { label: 'Late Arrival', color: 'warning', bg: '#fef7e0', text: '#b06000' };
    }
    if (formatted === 'leave' || formatted === 'approved leave') {
      return { label: 'On Leave', color: 'info', bg: '#e8f0fe', text: '#1a73e8' };
    }
    if (formatted === 'weekoff' || formatted === 'weekly off') {
      return { label: 'Weekly Off', color: 'default', bg: '#f1f3f4', text: '#5f6368' };
    }
    return { label: 'Absent', color: 'error', bg: '#fce8e6', text: '#c5221f' };
  };

  // Percentages calculated for performance micro-graphs
  const metricsPercentages = useMemo(() => {
    if (!liveCounts.total) return { presentPct: 0, latePct: 0, leavePct: 0, absentPct: 0 };
    return {
      presentPct: Math.round((liveCounts.present / liveCounts.total) * 100),
      latePct: Math.round((liveCounts.late / liveCounts.total) * 100),
      leavePct: Math.round((liveCounts.leave / liveCounts.total) * 100),
      absentPct: Math.round((liveCounts.absent / liveCounts.total) * 100),
    };
  }, [liveCounts]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fdfdfd', overflow: 'hidden', fontFamily: 'sans-serif' }}>
      
      {/* CLEAN BLUE TOP HEADER */}
      <Box sx={{ bgcolor: '#1a73e8', height: '56px', px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.06)', zIndex: 10, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Users size={22} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px', letterSpacing: '0.3px' }}>HR Attendance Hub</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="text" size="small" startIcon={<BarChart3 size={16} />} onClick={() => setShowAnalytics(!showAnalytics)} sx={{ color: '#fff', textTransform: 'none', fontWeight: 500 }}>
            {showAnalytics ? "Hide Analytics" : "Show Analytics"}
          </Button>
          <TextField type="month" size="small" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { height: '36px', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '6px', fontSize: '13px', color: '#fff', width: '150px', '& fieldset': { border: 'none' } } }} />
          <Button variant="contained" size="small" startIcon={<RefreshCw size={16} />} onClick={fetchAllEmployeeLogs} sx={{ bgcolor: '#fff', color: '#1a73e8', textTransform: 'none', fontWeight: 600, height: '36px', px: 2, borderRadius: '6px', boxShadow: 'none', '&:hover': { bgcolor: '#f1f3f4', boxShadow: 'none' } }}>Refresh Roster</Button>
        </Box>
      </Box>

      {/* DROPDOWN ANALYTICS & GRAPH PANEL */}
      {showAnalytics && (
        <Box sx={{ p: 3, pb: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Grid container spacing={2.5}>
            {/* Numeric KPI metrics Cards */}
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                {[
                  { title: 'Total Active Staff', count: liveCounts.total, color: '#1a73e8', icon: <Users size={18} color="#1a73e8" /> },
                  { title: 'Present Today', count: liveCounts.present, color: '#137333', icon: <UserCheck size={18} color="#137333" /> },
                  { title: 'Late Exceptions', count: liveCounts.late, color: '#b06000', icon: <Clock size={18} color="#b06000" /> },
                  { title: 'Absent / On Leave', count: liveCounts.leave + liveCounts.absent, color: '#c5221f', icon: <AlertCircle size={18} color="#c5221f" /> }
                ].map((card, idx) => (
                  <Card key={idx} sx={{ p: 2, borderRadius: '8px', boxShadow: 'none', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5f6368', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>{card.title}</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: card.color }}>{card.count}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: card.color + '10', p: 1, borderRadius: '50%' }}>{card.icon}</Box>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* LIVE UI BAR GRAPH SUMMARY */}
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 2, borderRadius: '8px', boxShadow: 'none', border: '1px solid #e0e0e0', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" sx={{ color: '#202124', fontWeight: 600, display: 'block', mb: 1 }}>TODAY'S ATTENDANCE DISTRIBUTION RATIO</Typography>
                
                {/* CSS Multi-Segment Stacked Bar Chart */}
                <Box sx={{ width: '100%', height: '14px', borderRadius: '10px', overflow: 'hidden', display: 'flex', bgcolor: '#e8eaed', mb: 1.5 }}>
                  <Box sx={{ width: `${metricsPercentages.presentPct}%`, bgcolor: '#137333' }} title={`Present: ${metricsPercentages.presentPct}%`} />
                  <Box sx={{ width: `${metricsPercentages.latePct}%`, bgcolor: '#b06000' }} title={`Late: ${metricsPercentages.latePct}%`} />
                  <Box sx={{ width: `${metricsPercentages.leavePct}%`, bgcolor: '#1a73e8' }} title={`Leave: ${metricsPercentages.leavePct}%`} />
                  <Box sx={{ width: `${metricsPercentages.absentPct}%`, bgcolor: '#c5221f' }} title={`Absent: ${metricsPercentages.absentPct}%`} />
                </Box>

                {/* Legend Chart Tags */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#137333' }} /><Typography variant="caption" sx={{ fontSize: '11px', color: '#5f6368' }}>Pres ({metricsPercentages.presentPct}%)</Typography></Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#b06000' }} /><Typography variant="caption" sx={{ fontSize: '11px', color: '#5f6368' }}>Late ({metricsPercentages.latePct}%)</Typography></Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1a73e8' }} /><Typography variant="caption" sx={{ fontSize: '11px', color: '#5f6368' }}>Leave ({metricsPercentages.leavePct}%)</Typography></Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#c5221f' }} /><Typography variant="caption" sx={{ fontSize: '11px', color: '#5f6368' }}>Abs ({metricsPercentages.absentPct}%)</Typography></Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* MAIN DATA WORKSPACE HUB */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', p: 3, gap: 3 }}>
        
        {/* LEFT WORKSPACE PANEL: ROSTER LIST VIEW */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', height: '100%', overflow: 'hidden' }}>
          
          {/* SEARCH AND TAB FILTER TOOLBAR */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f1f3f4', borderRadius: '6px', px: 1.5, py: 0.5, width: '280px', '&:focus-within': { bgcolor: '#fff', border: '1px solid #1a73e8' } }}>
              <Search size={16} style={{ color: '#5f6368', marginRight: '8px' }} />
              <TextField variant="standard" placeholder="Search employee name..." fullWidth value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }} />
            </Box>
            <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)} sx={{ minHeight: '36px', '& .MuiTabs-indicator': { bgcolor: '#1a73e8', height: '3px', borderRadius: '3px' }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '13px', minHeight: '36px', color: '#5f6368', px: 2 } }}>
              <Tab label="All Employees" value="all" />
              <Tab label="Present" value="present" />
              <Tab label="Late Arrivals" value="late" />
              <Tab label="Out of Office" value="leave" />
            </Tabs>
          </Box>

          {/* MAIN ROSTER TABLE CONTAINER */}
          <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress size={30} sx={{ color: '#1a73e8' }} /></Box>
            ) : (
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8f9fa', fontWeight: 600, color: '#3c4043', fontSize: '13px', borderBottom: '1px solid #e0e0e0' } }}>
                    <TableCell>Employee Details</TableCell>
                    <TableCell>Today Status</TableCell>
                    <TableCell align="center">Punch In</TableCell>
                    <TableCell align="center">Punch Out</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRoster.map((emp) => {
                    const isSelected = selectedEmployee?.id === emp.id;
                    const statusConfig = getStatusConfig(emp.currentStatus);
                    return (
                      <TableRow
                        key={emp.id}
                        hover
                        onClick={() => setSelectedEmployee(emp)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isSelected ? '#f4f8fe !important' : 'inherit',
                          '&:hover': { bgcolor: '#f8f9fa' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 600, bgcolor: '#1a73e8', color: '#fff' }}>
                              {emp.name.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#202124' }}>{emp.name}</Typography>
                              <Typography variant="caption" sx={{ color: '#70757a', display: 'block' }}>ID: #{emp.id} • {emp.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={statusConfig.label} 
                            size="small" 
                            sx={{ fontSize: '11px', fontWeight: 600, borderRadius: '4px', bgcolor: statusConfig.bg, color: statusConfig.text }} 
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#202124', fontWeight: 500 }}>{emp.clockInToday}</TableCell>
                        <TableCell align="center" sx={{ color: '#202124', fontWeight: 500 }}>{emp.clockOutToday}</TableCell>
                        <TableCell align="right">
                          <Button 
                            variant="text" 
                            size="small" 
                            startIcon={<Eye size={14} />} 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmployee(emp);
                              setDrillDownOpen(true);
                            }}
                            sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600 }}
                          >
                            Drill Down
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredRoster.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10, color: '#5f6368', fontSize: '14px' }}>No employee attendance matches the chosen filters.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Box>

        {/* RIGHT WORKSPACE PANEL: DRILL-DOWN PREVIEW SIDEBAR */}
        <Box sx={{ width: '380px', display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', height: '100%', overflow: 'hidden', flexShrink: 0 }}>
          {selectedEmployee ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

              {/* ACTION PREVIEW PROFILE IDENTIFIER */}
              <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa', textAlign: 'center' }}>
                <Avatar sx={{ width: 52, height: 52, mx: 'auto', mb: 1, bgcolor: '#e8f0fe', color: '#1a73e8', fontSize: '18px', fontWeight: 600 }}>
                  {selectedEmployee.name.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#202124', fontSize: '15px' }}>{selectedEmployee.name}</Typography>
                <Typography variant="body2" sx={{ color: '#5f6368', mb: 2, fontSize: '13px' }}>{selectedEmployee.email}</Typography>
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button variant="outlined" size="small" startIcon={<Mail size={14} />} href={`mailto:${selectedEmployee.email}`} sx={{ textTransform: 'none', borderRadius: '4px', fontSize: '12px' }}>Email</Button>
                  <Button variant="contained" size="small" endIcon={<ArrowRight size={14} />} onClick={() => setDrillDownOpen(true)} sx={{ textTransform: 'none', bgcolor: '#1a73e8', borderRadius: '4px', boxShadow: 'none', fontSize: '12px' }}>Full Analysis</Button>
                </Box>
              </Box>

              {/* OPERATIONAL SUMMARY STATS METRICS */}
              <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                {[
                  { label: 'Days Attended', val: selectedEmployee.daysPresent, color: '#137333' },
                  { label: 'Late Flags', val: selectedEmployee.daysLate, color: '#b06000' },
                  { label: 'Approved Leaves', val: selectedEmployee.daysLeave, color: '#1a73e8' },
                  { label: 'Absences (Unexcused)', val: selectedEmployee.unexcusedAbsences, color: '#c5221f' }
                ].map((stat, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #f1f3f4', borderRadius: '6px', p: 1.2, bgcolor: '#f8f9fa' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#5f6368' }}>{stat.label}</Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: stat.color, mt: 0.5 }}>{stat.val}</Typography>
                  </Box>
                ))}
              </Box>

              {/* PAYROLL VALUE CONSOLE METRIC BOX */}
              <Box sx={{ m: 2, bgcolor: '#e6f4ea', borderRadius: '6px', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#137333', fontWeight: 600 }}>Total Payable Days</Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#137333' }}>{selectedEmployee.payableDaysTotal} Days</Typography>
              </Box>

              <Divider />

              {/* MICRO ACTIVITY MONITOR FEED */}
              <Typography variant="caption" sx={{ px: 2, pt: 2, pb: 0.5, fontWeight: 700, color: '#202124', textTransform: 'uppercase' }}>Recent History Logs</Typography>
              <Box sx={{ flexGrow: 1, px: 2, pb: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #e0e0e0', ml: 1, pl: 2, gap: 2, pt: 1 }}>
                  {selectedEmployee.historicalLogs.slice(0, 5).map((log, index) => {
                    const timelineConfig = getStatusConfig(log.status);
                    return (
                      <Box key={index} sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ position: 'absolute', width: '6px', height: '6px', borderRadius: '50%', bgcolor: timelineConfig.text, left: '-21px', top: '7px', border: '2px solid #fff' }} />
                        <Box>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#202124' }}>{log.date}</Typography>
                          <Typography variant="caption" sx={{ color: '#5f6368', fontSize: '11px' }}>{log.clockIn} — {log.clockOut}</Typography>
                        </Box>
                        <Chip label={timelineConfig.label} size="small" sx={{ fontSize: '9px', height: '18px', borderRadius: '4px', bgcolor: timelineConfig.bg, color: timelineConfig.text }} />
                      </Box>
                    );
                  })}
                </Box>
              </Box>

            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#5f6368', p: 3, textAlign: 'center' }}>
              <CalendarDays size={28} strokeWidth={1.5} style={{ marginBottom: '8px' }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>Select an employee to inspect quick configurations.</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ========================================================
          DEEP DRILL-DOWN INTERACTIVE MODAL (COMPREHENSIVE AUDIT)
          ======================================================== */}
      {selectedEmployee && (
        <Dialog 
          open={drillDownOpen} 
          onClose={() => setDrillDownOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
        >
          <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202124' }}>Attendance Audit Drill-Down</Typography>
              <Typography variant="caption" sx={{ color: '#5f6368', fontSize: '13px' }}>
                Reviewing tracking logs for <strong>{selectedEmployee.name}</strong> • Month: {selectedMonth}
              </Typography>
            </Box>
            <Chip label={`Payable: ${selectedEmployee.payableDaysTotal} Days`} color="success" sx={{ fontWeight: 600 }} />
          </DialogTitle>
          
          <DialogContent dividers sx={{ bgcolor: '#f8f9fa', px: 3, py: 2 }}>
            <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
              {[
                { label: 'Total Present Days', val: selectedEmployee.daysPresent, col: '#137333', desc: 'Days clocked into office/remote' },
                { label: 'Late Grace Exceptions', val: selectedEmployee.daysLate, col: '#b06000', desc: 'Arrivals tracking past 11:15 AM IST' },
                { label: 'Approved Leave Time', val: selectedEmployee.daysLeave, col: '#1a73e8', desc: 'Paid corporate time-off records' },
                { label: 'Unexcused Absences', val: selectedEmployee.unexcusedAbsences, col: '#c5221f', desc: 'Missing activity log intervals' }
              ].map((m, i) => (
                <Card key={i} sx={{ p: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: m.col }}>{m.val}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202124', mt: 0.5 }}>{m.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', mt: 0.5, lineHeight: 1.2 }}>{m.desc}</Typography>
                </Card>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#202124', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={16} /> Complete Daily Timeline History
            </Typography>

            <TableContainer sx={{ bgcolor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', maxHeight: '350px', overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f1f3f4', fontWeight: 600, color: '#202124' } }}>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Punch In Time</TableCell>
                    <TableCell align="center">Punch Out Time</TableCell>
                    <TableCell align="right">Status Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmployee.historicalLogs.map((log, index) => {
                    const timelineConfig = getStatusConfig(log.status);
                    return (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{log.date}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{log.clockIn}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{log.clockOut}</TableCell>
                        <TableCell align="right">
                          <Chip label={timelineConfig.label} size="small" sx={{ fontSize: '10px', height: '18px', fontWeight: 600, borderRadius: '4px', bgcolor: timelineConfig.bg, color: timelineConfig.text }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          
          <DialogActions sx={{ p: 1.5 }}>
            <Button variant="outlined" onClick={() => setDrillDownOpen(false)} sx={{ textTransform: 'none', borderRadius: '6px' }}>
              Close Workspace Audit
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
};

export default HRAttendance;